import React from 'react';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import styles from './TagBadge.module.css';

/**
 * Individual tag badge component for displaying client type tags
 * @param {Object} props - Component props
 * @param {Object} props.tag - Tag object containing client type information
 * @param {string} props.tag.clientType - Client type name to display
 * @param {string} [props.tag.clientTypeDescription] - Optional description for tooltip
 * @param {number} props.tag.clientTypeId - Client type ID
 * @param {boolean} [props.removable=false] - Whether to show remove button
 * @param {Function} [props.onRemove] - Callback when remove button is clicked
 * @param {string} [props.size='medium'] - Size variant: 'small', 'medium', 'large'
 * @param {string} [props.variant='default'] - Color variant: 'default', 'primary', 'secondary'
 * @param {boolean} [props.disabled=false] - Whether the tag is disabled
 * @param {boolean} [props.loading=false] - Whether the tag is in loading state
 * @param {Function} [props.onClick] - Callback when tag is clicked
 * @param {string} [props.className] - Additional CSS classes
 */
const TagBadge = ({
    tag,
    removable = false,
    onRemove,
    size = 'medium',
    variant = 'default',
    disabled = false,
    loading = false,
    onClick,
    className = ''
}) => {
    const handleRemoveClick = (e) => {
        e.stopPropagation();
        if (onRemove && !disabled && !loading) {
            onRemove(tag.clientTypeId);
        }
    };

    const handleTagClick = (e) => {
        if (onClick && !disabled && !loading) {
            onClick(tag, e);
        }
    };

    const badgeClasses = [
        styles.tagBadge,
        styles[size],
        styles[variant],
        disabled && styles.disabled,
        loading && styles.loading,
        onClick && !disabled && !loading && styles.clickable,
        className
    ].filter(Boolean).join(' ');

    const displayText = tag.clientType || `Type ${tag.clientTypeId}`;
    const title = tag.clientTypeDescription 
        ? `${displayText}: ${tag.clientTypeDescription}`
        : displayText;

    return (
        <span
            className={badgeClasses}
            title={title}
            onClick={handleTagClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick && !disabled && !loading ? 0 : undefined}
            onKeyDown={onClick && !disabled && !loading ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTagClick(e);
                }
            } : undefined}
        >
            <span className={styles.tagText}>
                {displayText}
            </span>
            {loading ? (
                <FaSpinner className={styles.loadingSpinner} />
            ) : removable && !disabled && (
                <button
                    type="button"
                    className={styles.removeButton}
                    onClick={handleRemoveClick}
                    title={`Remove ${displayText}`}
                    disabled={disabled}
                >
                    <FaTimes />
                </button>
            )}
        </span>
    );
};

export default TagBadge;
